import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  Drawer,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { MdDashboard, MdAccountBalance } from 'react-icons/md';
import { RiUserSettingsFill } from 'react-icons/ri';
import { FaUserGroup, FaChevronDown } from 'react-icons/fa6';
import { IoSearchOutline, IoDocumentTextOutline, IoCheckmarkDoneOutline, IoLocationOutline, IoReaderOutline, IoStatsChartOutline } from 'react-icons/io5';
import axios from 'axios';
import { buildMenuModules } from '../utils/modules';
import { canAccessAnalytics } from '../config/analytics';

const drawerWidth = 240;

export default function Menu() {
  const [menuItemsFinal, setMenuItemsFinal] = useState([]);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const loggedInUser = sessionStorage.getItem('user_token_ra');
  const foundUser = loggedInUser ? JSON.parse(loggedInUser) : null;
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const getIcon = useMemo(
    () => (name, routing) => {
      const key = String(name || routing || '').toLowerCase();
      if (key.includes('dashboard')) return <MdDashboard />;
      if (key.includes('location')) return <IoLocationOutline />;
      if (key.includes('access')) return <RiUserSettingsFill />;
      if (key.includes('user')) return <FaUserGroup />;
      if (key.includes('event') || key.includes('log')) return <IoReaderOutline />;
      if (key.includes('insight')) return <IoStatsChartOutline />;
      if (key.includes('search-record') || key.includes('search record')) return <IoSearchOutline />;
      if (key.includes('open') || key.includes('report')) return <IoDocumentTextOutline />;
      if (key.includes('close')) return <IoCheckmarkDoneOutline />;
      // if (key.includes('form')) return <MdAccountBalance />;
      return <MdAccountBalance />;
    },
    []
  );

  const toggleSubMenu = (name) => {
    setOpenSubMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const filteredMenu = menuItemsFinal.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.submenu.some((sub) => sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    axios.get(`${baseURL}/get_modules`).then((res) => {
      setMenuItemsFinal(buildMenuModules(res.data, getIcon));
    }).catch(() => {
      setMenuItemsFinal([]);
    });
  }, [baseURL, getIcon]);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: 64,
          height: 'calc(100vh - 64px)',
          bgcolor: '#dfdfef',
          borderRight: '1px solid rgba(23, 57, 113, 0.08)',
          p: 1.75,
          overflowY: 'auto',
        },
      }}
    >
      <TextField
        placeholder="Search menu..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IoSearchOutline />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'rgba(255,255,255,0.55)',
          },
        }}
      />

      <Typography sx={{ px: 1.25, mb: 1, fontSize: 10, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: 'text.secondary' }}>
        Navigation
      </Typography>

      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {filteredMenu.map((val, index) => {
          const role = foundUser?.accessRole || {};
          const canView = role?.[val.accessKey || val.name]?.View;
          const hasVisibleChild = (val.submenu || []).some((sub) => role?.[sub.accessKey]?.View === true || role?.[sub.legacyAccessKey]?.View === true);
          const moduleKey = String(val.accessKey || val.name || '').trim().toLowerCase();
          const isReportsModule = moduleKey === 'reports';
          const isStrictStandaloneModule = ['search records', 'search-records', 'insights'].includes(moduleKey);
          if (isReportsModule && !hasVisibleChild) return null;
          if (isStrictStandaloneModule && canView !== true) return null;
          if (canView === false && !hasVisibleChild) return null;

          const hasSubmenu = val.submenu && val.submenu.length > 0;
          const isOpen = openSubMenus[val.name];

          if (hasSubmenu) {
            return (
              <Box key={`${val.name}-${index}`}>
                <ListItemButton
                  onClick={() => toggleSubMenu(val.name)}
                  sx={{
                    borderRadius: 2.5,
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{val.icon}</ListItemIcon>
                  <ListItemText primary={val.name} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }} />
                  <Box sx={{ display: 'flex', transition: 'transform 0.2s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <FaChevronDown size={12} />
                  </Box>
                </ListItemButton>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ ml: 2, pl: 1.25, borderLeft: '1px solid rgba(23,57,113,0.12)' }}>
                    {val.submenu.map((sub, subIndex) => {
                      const role = foundUser?.accessRole || {};
                      const qualifiedPermission = role?.[sub.accessKey];
                      const legacyPermission = role?.[sub.legacyAccessKey];
                      const parentPermission = role?.[val.accessKey || val.name];
                      const isReportsSubmodule = String(val.accessKey || val.name || '').trim().toLowerCase() === 'reports';
                      const canViewSubmenu = qualifiedPermission
                        ? qualifiedPermission.View !== false
                        : legacyPermission
                          ? legacyPermission.View !== false
                          : isReportsSubmodule
                            ? false
                            : parentPermission?.View !== false;
                      if (!canViewSubmenu) return null;
                      return (
                      <ListItemButton
                        key={`${sub.name}-${subIndex}`}
                        component={NavLink}
                        to={sub.routing}
                        sx={{
                          borderRadius: 2,
                          py: 0.75,
                          '&.active': {
                            bgcolor: 'rgba(47, 111, 208, 0.14)',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ListItemText primary={sub.name} primaryTypographyProps={{ fontSize: 13 }} />
                      </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          return (
            <ListItemButton
              key={`${val.name}-${index}`}
              component={NavLink}
              to={val.routing}
              onClick={() => setOpenSubMenus({})}
              sx={{
                borderRadius: 2.5,
                color: 'text.primary',
                '&.active': {
                  bgcolor: 'rgba(47, 111, 208, 0.14)',
                  color: 'primary.main',
                  border: '1px solid rgba(47, 111, 208, 0.18)',
                },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{val.icon}</ListItemIcon>
              <ListItemText primary={val.name} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }} />
            </ListItemButton>
          );
        })}
        {canAccessAnalytics(foundUser) && 'analytics'.includes(searchTerm.toLowerCase()) && (
          <ListItemButton
            component={NavLink}
            to="/analytics"
            onClick={() => setOpenSubMenus({})}
            sx={{
              borderRadius: 2.5,
              color: 'text.primary',
              '&.active': {
                bgcolor: 'rgba(47, 111, 208, 0.14)',
                color: 'primary.main',
                border: '1px solid rgba(47, 111, 208, 0.18)',
              },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}><IoStatsChartOutline /></ListItemIcon>
            <ListItemText primary="Analytics" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }} />
          </ListItemButton>
        )}
      </List>
    </Drawer>
  );
}
